import React, { useMemo, useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";

import Reveal from "../components/Reveal";
import TypeTile from "../components/TypeTile";
import FinalCTA from "../components/sections/FinalCTA";
import { fetchMarbleTypes } from "../lib/api";
import { useSampleRequest } from "../context/SampleRequestContext";

const SOURCE_FILTERS = ["All", "Indian", "Imported"];
const COLOUR_FILTERS = ["All", "Beige / Cream", "White", "Grey", "Green", "Exotic", "Other"];

const FilterDropdown = ({ label, value, options, onChange, testId }) => (
  <div className="flex-1 md:max-w-[240px]">
    <label
      htmlFor={testId}
      className="block font-sans text-[10px] uppercase tracking-[0.32em] text-brand-gold mb-2.5"
    >
      {label}
    </label>
    <div className="relative">
      <select
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-brand-surface border border-brand-cream/20 text-brand-cream text-sm font-sans px-4 py-3 pr-10 transition-colors duration-300 hover:border-brand-cream/40 focus:outline-none focus:border-brand-gold/60 cursor-pointer"
        style={{ borderRadius: "5px" }}
      >
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-brand-charcoal text-brand-cream">
            {opt}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gold"
      />
    </div>
  </div>
);

const SkeletonTile = () => (
  <div
    data-testid="collection-skeleton-tile"
    className="aspect-[4/5] bg-brand-surface animate-pulse ring-1 ring-white/5"
  />
);

// Module-level cache: persists across mounts/unmounts of the Collection
// component (e.g. back navigation) so the page renders instantly with the
// already-fetched data instead of re-fetching and re-showing the skeleton.
let cachedMarbleTypes = null;

export const Collection = () => {
  const [marbleTypes, setMarbleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("All");
  const [colourFilter, setColourFilter] = useState("All");
  const { openModal } = useSampleRequest();

  useEffect(() => {
    let mounted = true;

    if (cachedMarbleTypes !== null) {
      setMarbleTypes(cachedMarbleTypes);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const data = await fetchMarbleTypes();
        const result = Array.isArray(data) ? data : [];
        cachedMarbleTypes = result;
        if (mounted) {
          setMarbleTypes(result);
        }
      } catch (err) {
        console.error("Failed to load marble types:", err);
        if (mounted) setLoadError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return marbleTypes.filter((m) => {
      const sourceOk = sourceFilter === "All" || m.indian_or_imported === sourceFilter;
      const colourOk = colourFilter === "All" || m.color === colourFilter;
      return sourceOk && colourOk;
    });
  }, [marbleTypes, sourceFilter, colourFilter]);

  const noMarbleTypesAtAll = !loading && !loadError && marbleTypes.length === 0;
  const noFilterMatches = !loading && !loadError && marbleTypes.length > 0 && filtered.length === 0;

  return (
    <main data-testid="collection-page" className="bg-brand-charcoal">
      {/* HEADER */}
      <section
        data-testid="collection-header"
        className="relative pt-12 md:pt-20 pb-12 md:pb-16"
      >
        <div className="editorial-container">
          <Reveal>
            <div className="flex items-center gap-4 mb-7">
              <span className="h-px w-10 bg-brand-gold/70" />
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold">
                The Collection
              </p>
            </div>
            <h1
              data-testid="collection-page-heading"
              className="font-serif font-light text-balance text-brand-cream leading-[1.02] tracking-tight max-w-5xl"
              style={{ fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)" }}
            >
              Marble, hand-picked from the world&apos;s{" "}
              <span className="italic text-brand-gold/90">finest quarries.</span>
            </h1>
            <p className="mt-7 md:mt-9 font-sans text-base md:text-lg leading-relaxed text-brand-cream/65 max-w-2xl">
              Italian classics, Indian heritage, Spanish warmth, Turkish
              character - every type carefully chosen, every slab verified.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FILTER BARS */}
      <section
        data-testid="collection-filter-bar"
        className="relative border-t border-white/5"
      >
        <div className="editorial-container py-7 md:py-9">
          <div className="flex flex-col sm:flex-row gap-5 sm:gap-6">
            <FilterDropdown
              label="Type"
              value={sourceFilter}
              options={SOURCE_FILTERS}
              onChange={setSourceFilter}
              testId="source-filter-select"
            />
            <FilterDropdown
              label="Color"
              value={colourFilter}
              options={COLOUR_FILTERS}
              onChange={setColourFilter}
              testId="color-filter-select"
            />
          </div>
        </div>
      </section>

      {/* GRID */}
      <section
        data-testid="collection-grid-section"
        className="relative border-t border-white/5 pt-10 md:pt-14 pb-24 md:pb-32"
      >
        <div className="editorial-container">
          {loading ? (
            <div data-testid="collection-skeleton-grid" className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonTile key={i} />
              ))}
            </div>
          ) : noMarbleTypesAtAll || loadError ? (
            <div
              data-testid="collection-empty-state"
              className="min-h-[40vh] flex flex-col items-center justify-center text-center py-20"
            >
              <span className="h-px w-10 bg-brand-gold/40 mb-7" />
              <p className="font-serif italic text-2xl md:text-3xl text-brand-cream/70 max-w-md">
                Our collection is being updated. Check back soon.
              </p>
              <p className="mt-3 font-sans text-sm text-brand-cream/45 max-w-sm">
                In the meantime, WhatsApp us and we&apos;ll share our full catalogue.
              </p>
              <a
                href="https://wa.me/918796564619"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="collection-empty-whatsapp"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-brand-gold text-brand-charcoal font-sans text-xs uppercase tracking-[0.2em] font-medium transition-all hover:bg-brand-gold/90"
                style={{ borderRadius: "5px" }}
              >
                WhatsApp Us
              </a>
            </div>
          ) : noFilterMatches ? (
            <div
              data-testid="collection-no-filter-match"
              className="min-h-[30vh] flex flex-col items-center justify-center text-center py-20"
            >
              <span className="h-px w-10 bg-brand-gold/40 mb-7" />
              <p className="font-serif italic text-2xl md:text-3xl text-brand-cream/70 max-w-md">
                No marbles match this combination. Try a different filter.
              </p>
            </div>
          ) : (
            <div
              key={`${sourceFilter}-${colourFilter}`}
              data-testid="collection-grid"
              className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 animate-fade-in"
            >
              {/* Guard: TypeTile (and the MarbleImage it mounts) is only ever
                  rendered once loading is false and marbleTypes is populated -
                  reaching this branch already guarantees both, since every
                  earlier branch (skeleton / empty / no-filter-match) handles
                  the loading / unpopulated / filtered-empty cases first. */}
              {!loading && marbleTypes.length > 0 &&
                filtered.map((m, i) => (
                  <TypeTile
                    key={m.marble_sno}
                    marbleType={m}
                    revealDelay={Math.min(i * 0.04, 0.32)}
                  />
                ))}
            </div>
          )}

          {!loading && !noMarbleTypesAtAll && !loadError && (
            <p className="mt-12 md:mt-16 font-sans text-[11px] uppercase tracking-[0.32em] text-brand-cream/35 text-center">
              Showing {filtered.length} of {marbleTypes.length} marble{marbleTypes.length === 1 ? "" : "s"}
            </p>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCTA
        eyebrow="· Personal Guidance ·"
        heading={
          <>
            Can&apos;t decide?{" "}
            <span className="italic">Let us help.</span>
          </>
        }
        subtext="Tell us about your space. We'll recommend the perfect marble - and bring samples to your door."
        primary={{ label: "Request Free Samples", onClick: () => openModal([], "collection_final_cta") }}
        secondary={{
          label: "WhatsApp Us",
          href: "https://wa.me/918796564619",
          external: true,
        }}
        testIdPrimary="collection-cta-request-samples"
        testIdSecondary="collection-cta-whatsapp"
      />
    </main>
  );
};

export default Collection;
